import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Building2,
  Users,
  TrendingUp,
  Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Cases = ({ onDemoClick }) => {
  const cases = [
    {
      company: 'Indústria MetalTech',
      segment: 'Indústria Metalúrgica',
      employees: '2.500 colaboradores',
      challenge: 'Padronizar treinamentos de segurança em 5 unidades diferentes.',
      solution: 'Plataforma EAD + Produção de 15 cursos de SST personalizados.',
      results: [
        '40% de redução em acidentes de trabalho',
        '95% de taxa de conclusão dos treinamentos',
        'Economia de R$ 200k/ano em treinamentos presenciais',
      ],
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop',
      testimonial: {
        quote: 'A CSP entendeu nossas necessidades e entregou uma solução que realmente funciona. O engajamento dos colaboradores superou todas as expectativas.',
        author: 'Carlos Mendes',
        role: 'Diretor Industrial',
      },
    },
    {
      company: 'Rede Saúde+',
      segment: 'Saúde',
      employees: '8.000 colaboradores',
      challenge: 'Capacitar equipes de enfermagem em novos protocolos rapidamente.',
      solution: 'Plataforma EAD com trilhas de aprendizagem e certificação.',
      results: [
        '3x mais rápido na atualização de protocolos',
        '100% de compliance em auditorias',
        'NPS de 4.8/5 dos colaboradores',
      ],
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop',
      testimonial: {
        quote: 'Conseguimos treinar milhares de profissionais em tempo recorde. A plataforma é intuitiva e os relatórios nos dão visibilidade total.',
        author: 'Dra. Ana Paula Silva',
        role: 'Diretora de Enfermagem',
      },
    },
    {
      company: 'Banco Digital FastPay',
      segment: 'Financeiro',
      employees: '1.200 colaboradores',
      challenge: 'Criar universidade corporativa para onboarding e desenvolvimento.',
      solution: 'Plataforma completa + Produção de cursos + Gamificação.',
      results: [
        '50% de redução no tempo de onboarding',
        'Aumento de 35% na retenção de talentos',
        'ROI de 400% no primeiro ano',
      ],
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
      testimonial: {
        quote: 'A universidade corporativa se tornou um diferencial competitivo para atrair talentos. O retorno sobre o investimento foi impressionante.',
        author: 'Rodrigo Costa',
        role: 'VP de Pessoas',
      },
    },
    {
      company: 'Construtora Habitat',
      segment: 'Construção Civil',
      employees: '4.000 colaboradores',
      challenge: 'Adequar-se à NR-01 psicossocial com equipe dispersa em obras.',
      solution: 'Ferramenta NR-01 + Treinamentos sobre saúde mental.',
      results: [
        '100% de conformidade com a nova norma',
        'Redução de 25% em afastamentos por estresse',
        'Documentação completa para auditorias',
      ],
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop',
      testimonial: {
        quote: 'A ferramenta nos ajudou a organizar todo o processo de NR-01. Agora temos visibilidade dos riscos psicossociais em todas as obras.',
        author: 'Fernanda Lima',
        role: 'Gerente de SST',
      },
    },
  ];

  const stats = [
    { value: '500+', label: 'Empresas Atendidas', icon: Building2 },
    { value: '50k+', label: 'Colaboradores Treinados', icon: Users },
    { value: '98%', label: 'Taxa de Satisfação', icon: TrendingUp },
  ];

  return (
    <div data-testid="cases-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 text-sm font-medium mb-6">
              Cases de Sucesso
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Resultados que{' '}
              <span className="text-orange-400">transformam</span>{' '}
              empresas
            </h1>
            <p className="text-lg text-slate-400">
              Conheça histórias reais de empresas que revolucionaram sua educação corporativa 
              com as soluções da CSP.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-50 mb-4">
                  <stat.icon className="h-7 w-7 text-blue-600" />
                </div>
                <p className="text-4xl font-bold text-slate-900 mb-1">{stat.value}</p>
                <p className="text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {cases.map((caseStudy, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden border-0 shadow-xl bg-white">
                  <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? '' : ''}`}>
                    <div className={`relative h-64 lg:h-auto min-h-[300px] ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img
                        src={caseStudy.image}
                        alt={caseStudy.company}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
                      <div className="absolute bottom-6 left-6">
                        <span className="inline-block px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-medium mb-2">
                          {caseStudy.segment}
                        </span>
                        <h3 className="text-2xl font-bold text-white">{caseStudy.company}</h3>
                        <p className="text-white/80 text-sm">{caseStudy.employees}</p>
                      </div>
                    </div>

                    <CardContent className={`p-8 lg:p-10 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Desafio
                          </h4>
                          <p className="text-slate-700">{caseStudy.challenge}</p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Solução
                          </h4>
                          <p className="text-slate-700">{caseStudy.solution}</p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Resultados
                          </h4>
                          <ul className="space-y-2">
                            {caseStudy.results.map((result, i) => (
                              <li key={i} className="flex items-center gap-2 text-slate-700">
                                <span className="w-2 h-2 rounded-full bg-green-500" />
                                {result}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="pt-6 border-t border-slate-100">
                          <div className="flex gap-4">
                            <Quote className="h-8 w-8 text-blue-200 flex-shrink-0" />
                            <div>
                              <p className="text-slate-600 italic mb-3">
                                "{caseStudy.testimonial.quote}"
                              </p>
                              <p className="font-semibold text-slate-900">
                                {caseStudy.testimonial.author}
                              </p>
                              <p className="text-sm text-slate-500">
                                {caseStudy.testimonial.role}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Sua empresa pode ser o próximo case de sucesso
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Fale com nossos especialistas e descubra como podemos ajudar sua empresa 
              a alcançar resultados extraordinários.
            </p>
            <Button
              onClick={onDemoClick}
              data-testid="cases-cta-demo-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full text-lg font-semibold"
            >
              Solicitar Demonstração
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
